import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * 좌측 두 칸의 폭.
 *
 * 시안의 176 / 376은 **기본값이지 규격이 아니다.** 13인치에서 넉넉한 목록이
 * 27인치에서는 우표만 하고, 한국어 이름과 긴 메일 주소는 잘리는 지점도 제각각이다.
 * 고정폭으로 못 박아두면 화면이 남아도는데도 미리보기가 한 줄에서 끊긴다.
 *
 * 바꾼 값은 **이 기기에 남는다.** 창을 열 때마다 다시 끌어야 한다면 조절할 수
 * 있다는 사실 자체가 값을 못 한다. 계정에 저장하지 않는 이유는 화면 크기가
 * 기기마다 달라서다 — 노트북에서 좁힌 것이 데스크톱까지 따라오면 손해다.
 */

const STORAGE_KEY = "catmail.panel-widths";

/** 시안 값. 되돌리기(더블클릭)가 돌아오는 자리이기도 하다. */
export const DEFAULT_WIDTHS = { rail: 176, list: 376 } as const;

/**
 * 각 칸이 제 노릇을 하는 범위.
 *
 * 레일은 "1:1 대화"가 한 줄에 들어가는 폭이 하한이고, 그보다 넓어져 봐야
 * 아이콘과 짧은 낱말뿐이라 상한도 낮다. 목록은 미리보기 한 줄이 읽히는 폭이
 * 하한이다.
 */
const LIMITS = {
  rail: { min: 132, max: 264 },
  list: { min: 288, max: 560 },
} as const;

/** 대화창에 최소한 이만큼은 남긴다. 말풍선이 이보다 좁으면 읽기 어렵다. */
const MIN_MAIN = 420;

export type PanelKey = keyof typeof LIMITS;
export type PanelWidths = Record<PanelKey, number>;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(Math.round(value), min), Math.max(min, max));
}

/** 창 폭까지 감안한 목록의 상한. 늘리다 대화창을 잡아먹지 않게 한다. */
function listMaxFor(railWidth: number): number {
  const room =
    typeof window === "undefined"
      ? LIMITS.list.max
      : window.innerWidth - railWidth - MIN_MAIN;
  return Math.min(LIMITS.list.max, Math.max(LIMITS.list.min, room));
}

function normalize(input: Partial<PanelWidths>): PanelWidths {
  const rail = clamp(
    input.rail ?? DEFAULT_WIDTHS.rail,
    LIMITS.rail.min,
    LIMITS.rail.max,
  );
  return {
    rail,
    list: clamp(input.list ?? DEFAULT_WIDTHS.list, LIMITS.list.min, listMaxFor(rail)),
  };
}

function readStored(): PanelWidths {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return normalize({});

    const parsed = JSON.parse(raw) as Partial<PanelWidths>;
    return normalize({
      ...(typeof parsed.rail === "number" ? { rail: parsed.rail } : {}),
      ...(typeof parsed.list === "number" ? { list: parsed.list } : {}),
    });
    // 사생활 보호 모드처럼 저장소를 못 쓰는 브라우저가 있다. 기본값으로 간다.
  } catch {
    return normalize({});
  }
}

export function usePanelWidths() {
  const [widths, setWidths] = useState<PanelWidths>(readStored);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
    } catch {
      // 저장 못 해도 이번 세션 동안은 조절한 대로 쓴다.
    }
  }, [widths]);

  // 창을 줄이면 상한이 함께 줄어든다. 그대로 두면 대화창이 화면 밖으로 밀린다.
  useEffect(() => {
    const onResize = () => setWidths((previous) => normalize(previous));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const setWidth = useCallback((key: PanelKey, value: number) => {
    setWidths((previous) => normalize({ ...previous, [key]: value }));
  }, []);

  const resetWidth = useCallback((key: PanelKey) => {
    setWidths((previous) => normalize({ ...previous, [key]: DEFAULT_WIDTHS[key] }));
  }, []);

  // 손잡이가 aria-valuemin/max로 읽어줄 값. 목록 쪽은 창 크기에 따라 변한다.
  const limits = useMemo(
    () => ({
      rail: LIMITS.rail,
      list: { min: LIMITS.list.min, max: listMaxFor(widths.rail) },
    }),
    [widths.rail],
  );

  return { widths, limits, setWidth, resetWidth };
}
